import { useState } from 'react';
import {
  useCreateMenuCategoryMutation,
  useCreateModifierGroupMutation,
  useCreateModifierMutation,
  useDeleteMenuCategoryMutation,
  useDeleteModifierGroupMutation,
  useDeleteModifierMutation,
  useReorderMenuCategoriesMutation,
  useSetItemModifierGroupsMutation,
  useUpdateMenuCategoryMutation,
  useUpdateModifierGroupMutation,
  useUpdateModifierMutation,
  type MenuCategoryRecord,
  type MenuItemRecord,
  type ModifierGroupRecord,
  type ModifierRecord
} from '@/store/api';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, Field, Panel } from '@/components/ui';
import { formatMoney } from '@/utils/format';

/**
 * Marks who owns a record.
 *
 * The distinction matters operationally: a POS-managed price changes in the
 * till and arrives on the next sync, so editing it here would produce a value
 * that silently reverts — and, in the window before it does, a phone line
 * quoting a number the kitchen will not honour.
 */
export function SourceTag({ externalPosId, provider }: { externalPosId?: string; provider?: string }) {
  return externalPosId ? (
    <span className="rounded bg-jade-soft px-1.5 py-0.5 text-micro text-jade">
      {provider && provider !== 'NONE' ? `${provider} managed` : 'POS managed'}
    </span>
  ) : (
    <span className="rounded bg-paper px-1.5 py-0.5 text-micro text-slate">Callwaiter managed</span>
  );
}

export function CategoriesPanel({
  locationId,
  categories,
  provider
}: {
  locationId: string;
  categories: MenuCategoryRecord[];
  provider?: string;
}) {
  const dispatch = useAppDispatch();
  const [createCategory, { isLoading: creating }] = useCreateMenuCategoryMutation();
  const [updateCategory] = useUpdateMenuCategoryMutation();
  const [deleteCategory] = useDeleteMenuCategoryMutation();
  const [reorder] = useReorderMenuCategoriesMutation();

  const [name, setName] = useState('');

  const add = async () => {
    if (!name.trim()) return;
    const result = await createCategory({ locationId, name: name.trim() });

    dispatch(
      'error' in result
        ? pushToast('Could not add that category.', 'error')
        : pushToast('Category added.', 'success')
    );
    if (!('error' in result)) setName('');
  };

  const rename = async (category: MenuCategoryRecord) => {
    const next = window.prompt('Rename this category', category.name);
    if (!next?.trim()) return;

    const result = await updateCategory({ id: category._id, name: next.trim() });
    if ('error' in result) dispatch(pushToast('Could not rename that category.', 'error'));
  };

  const remove = async (category: MenuCategoryRecord) => {
    const result = await deleteCategory(category._id);
    dispatch(
      'error' in result
        ? pushToast('Could not remove that category.', 'error')
        : pushToast('Category removed. Its items are still on your menu.', 'success')
    );
  };

  /** Simple move-up/down rather than drag: it works on a phone in a kitchen. */
  const move = async (index: number, direction: -1 | 1) => {
    const next = [...categories];
    const target = next[index + direction];
    const current = next[index];
    if (!target || !current) return;

    next[index] = target;
    next[index + direction] = current;
    await reorder({ order: next.map((entry) => entry._id) });
  };

  return (
    <Panel title="Categories">
      <ul className="divide-y divide-rule">
        {categories.map((category, index) => (
          <li key={category._id} className="flex items-center gap-3 px-4 py-2.5">
            <span className="text-small text-ink">{category.name}</span>
            <SourceTag externalPosId={category.externalPosId} provider={provider} />

            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                className="text-micro text-slate disabled:opacity-30"
              >
                Up
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === categories.length - 1}
                className="text-micro text-slate disabled:opacity-30"
              >
                Down
              </button>

              {category.externalPosId ? (
                <span className="text-micro text-slate">Edit in your POS</span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => rename(category)}
                    className="text-micro text-jade underline underline-offset-2"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(category)}
                    className="text-micro text-rust underline underline-offset-2"
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
          </li>
        ))}

        {categories.length === 0 ? (
          <li className="px-4 py-3 text-small text-slate">
            No categories yet. They group your menu but are not required for phone ordering.
          </li>
        ) : null}
      </ul>

      <div className="flex items-end gap-3 border-t border-rule px-4 py-3">
        <Field label="New category">
          <input
            className="input"
            value={name}
            placeholder="Dosas"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Button onClick={add} loading={creating} disabled={!name.trim()}>
          Add
        </Button>
      </div>
    </Panel>
  );
}

export function ModifierGroupsPanel({
  locationId,
  groups,
  modifiers,
  items,
  provider
}: {
  locationId: string;
  groups: ModifierGroupRecord[];
  modifiers: ModifierRecord[];
  items: MenuItemRecord[];
  provider?: string;
}) {
  const dispatch = useAppDispatch();
  const [createGroup, { isLoading: creatingGroup }] = useCreateModifierGroupMutation();
  const [updateGroup] = useUpdateModifierGroupMutation();
  const [deleteGroup] = useDeleteModifierGroupMutation();
  const [createModifier] = useCreateModifierMutation();
  const [updateModifier] = useUpdateModifierMutation();
  const [deleteModifier] = useDeleteModifierMutation();
  const [setItemGroups] = useSetItemModifierGroupsMutation();

  const [groupName, setGroupName] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const addGroup = async () => {
    if (!groupName.trim()) return;
    const result = await createGroup({ locationId, name: groupName.trim() });

    dispatch(
      'error' in result
        ? pushToast('Could not add that group.', 'error')
        : pushToast('Modifier group added.', 'success')
    );
    if (!('error' in result)) setGroupName('');
  };

  const addModifier = async (group: ModifierGroupRecord) => {
    const name = window.prompt(`Add a choice to ${group.name}`);
    if (!name?.trim()) return;

    const price = window.prompt('Extra charge for this choice, in dollars', '0');
    const priceCents = Math.round(Number(price ?? 0) * 100);

    if (!Number.isFinite(priceCents) || priceCents < 0) {
      dispatch(pushToast('That price was not a number.', 'error'));
      return;
    }

    const result = await createModifier({ groupId: group._id, name: name.trim(), priceCents });
    if ('error' in result) dispatch(pushToast('Could not add that choice.', 'error'));
  };

  const toggleItemGroup = async (item: MenuItemRecord, groupId: string, on: boolean) => {
    const current = ((item as unknown as { modifierGroupIds?: string[] }).modifierGroupIds ?? []).map(
      String
    );
    const next = on ? [...new Set([...current, groupId])] : current.filter((id) => id !== groupId);

    const result = await setItemGroups({ id: item._id, modifierGroupIds: next });
    if ('error' in result) dispatch(pushToast('Could not update that item.', 'error'));
  };

  return (
    <Panel title="Modifier groups">
      <p className="border-b border-rule px-4 py-2.5 text-small text-slate">
        Groups are the choices the line offers, like spice level or extras. Required groups are
        asked for before an item is added to the order.
      </p>

      <ul className="divide-y divide-rule">
        {groups.map((group) => {
          const options = modifiers.filter((modifier) => modifier.groupId === group._id);
          const posManaged = Boolean(group.externalPosId);
          const isOpen = expanded === group._id;

          return (
            <li key={group._id} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : group._id)}
                  className="text-small text-ink underline underline-offset-2"
                >
                  {group.name}
                </button>
                <SourceTag externalPosId={group.externalPosId} provider={provider} />
                <span className="text-micro text-slate">
                  {group.required ? 'Required' : 'Optional'} · choose {group.minSelections}–
                  {group.maxSelections} · {options.length}{' '}
                  {options.length === 1 ? 'choice' : 'choices'}
                </span>

                {!posManaged ? (
                  <div className="ml-auto flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-micro text-slate">
                      <input
                        type="checkbox"
                        checked={group.required}
                        onChange={(event) =>
                          updateGroup({
                            id: group._id,
                            required: event.target.checked,
                            minSelections: event.target.checked ? Math.max(1, group.minSelections) : 0
                          })
                        }
                        className="h-3.5 w-3.5 accent-[#0B7A5A]"
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      onClick={() => deleteGroup(group._id)}
                      className="text-micro text-rust underline underline-offset-2"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <span className="ml-auto text-micro text-slate">Edit in your POS</span>
                )}
              </div>

              {isOpen ? (
                <div className="mt-3 space-y-3 border-l-2 border-rule pl-3">
                  {!posManaged ? (
                    <div className="flex items-center gap-3">
                      <Field label="Minimum">
                        <input
                          type="number"
                          min="0"
                          className="input tabular w-20"
                          value={group.minSelections}
                          onChange={(event) =>
                            updateGroup({ id: group._id, minSelections: Number(event.target.value) })
                          }
                        />
                      </Field>
                      <Field label="Maximum">
                        <input
                          type="number"
                          min="1"
                          className="input tabular w-20"
                          value={group.maxSelections}
                          onChange={(event) =>
                            updateGroup({ id: group._id, maxSelections: Number(event.target.value) })
                          }
                        />
                      </Field>
                    </div>
                  ) : null}

                  <ul className="divide-y divide-rule">
                    {options.map((modifier) => (
                      <li key={modifier._id} className="flex items-center gap-3 py-2">
                        <span className="text-small text-ink">{modifier.name}</span>
                        <span className="tabular text-small text-slate">
                          {modifier.priceCents > 0 ? `+${formatMoney(modifier.priceCents)}` : 'No charge'}
                        </span>
                        <SourceTag externalPosId={modifier.externalPosId} provider={provider} />

                        <div className="ml-auto flex items-center gap-3">
                          <label className="flex items-center gap-1.5 text-micro text-slate">
                            <input
                              type="checkbox"
                              checked={modifier.available}
                              onChange={(event) =>
                                updateModifier({ id: modifier._id, available: event.target.checked })
                              }
                              disabled={Boolean(modifier.externalPosId)}
                              className="h-3.5 w-3.5 accent-[#0B7A5A]"
                            />
                            Available
                          </label>

                          {modifier.externalPosId ? null : (
                            <button
                              type="button"
                              onClick={() => deleteModifier(modifier._id)}
                              className="text-micro text-rust underline underline-offset-2"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>

                  {!posManaged ? (
                    <button
                      type="button"
                      onClick={() => addModifier(group)}
                      className="text-small text-jade underline underline-offset-2"
                    >
                      Add a choice
                    </button>
                  ) : null}

                  <div>
                    <p className="text-micro text-slate">Applies to these items:</p>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {items.map((item) => {
                        const assigned = (
                          (item as unknown as { modifierGroupIds?: string[] }).modifierGroupIds ?? []
                        )
                          .map(String)
                          .includes(group._id);

                        return (
                          <label
                            key={item._id}
                            className="flex items-center gap-1.5 rounded border border-rule px-2 py-1 text-micro text-slate"
                          >
                            <input
                              type="checkbox"
                              checked={assigned}
                              onChange={(event) =>
                                toggleItemGroup(item, group._id, event.target.checked)
                              }
                              className="h-3.5 w-3.5 accent-[#0B7A5A]"
                            />
                            {item.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}

        {groups.length === 0 ? (
          <li className="px-4 py-3 text-small text-slate">
            No modifier groups yet. Add one if callers need to choose, for example a spice level.
          </li>
        ) : null}
      </ul>

      <div className="flex items-end gap-3 border-t border-rule px-4 py-3">
        <Field label="New group">
          <input
            className="input"
            placeholder="Spice level"
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
          />
        </Field>
        <Button onClick={addGroup} loading={creatingGroup} disabled={!groupName.trim()}>
          Add
        </Button>
      </div>
    </Panel>
  );
}
